import { Component } from "react";
import { bindActionCreators } from "redux";
import { bindAction, type NotifyStepSettings, type Group, type User } from "interfaces";
import { connect, type ConnectedProps } from "react-redux";
import { Button } from "components/buttons/button/Button";
import { isEmpty, some } from "lodash";

import {
  type AddPeopleToContentTypes,
  AssignmentPeopleContext,
  RolePermissions,
  type SortingDirection,
  ViewType,
} from "../../../../enums";
import { Awareness, Enrollment } from "../../../../enums/groups";
import { ContentAssignmentModal, UsersAssignmentModal } from "../../../../components/assignmentModals";
import { DeleteGroupsConfirmationModal } from "../../../../components/people";
import { GroupList } from "../../../../components/people/groupList";
import { usersFilter } from "components/filterForms/UsersFilterForm/usersFilter";
import { GroupsOverviewHeader } from "./GroupsOverviewHeader/GroupsOverviewHeader";
import { pluralize } from "../../../../utils/stringUtils";
import userListUtils from "../../../../utils/userListUtils";
import backgroundTask from "../../../BackgroundTasks/backgroundTask";
import dataService from "../../../Application/services/dataServices/dataService";
import Restricted from "../../../Application/Restricted";

import * as addToContentActionsRedux from "../../state/slices/addToContentSlice";
import * as backgroundTasksActionsRedux from "../../../BackgroundTasks/state/backgroundTasksActions";
import * as editGroupActionsRedux from "../EditGroup/editGroupActions";
import * as filterActionsRedux from "./state/filterActions";
import * as groupFiltersActionsRedux from "./state/groupFiltersSlice";
import * as groupsOverviewActionsRedux from "./state/groupsOverviewActions";
import * as notificationsActionsRedux from "../../../Notifications/state/notificationsActions";
import GroupsNoResults from "./GroupsNoResults/GroupsNoResults";
import RestrictedByTooltip from "../../../../components/restrictedByTooltip/RestrictedByTooltip";
import { addUsersToGroups } from "../../state/thunks/peopleThunk";
import { withRouter, type WithRouterProps } from "../../../../adapters/withRouter/withRouter";
import { shouldShowModalForGroupMembership } from "features/Licensing/ContentAssignmentModalLicensingSteps/state/thunks/licensingModalThunk";
import { reset } from "features/Licensing/ContentAssignmentModalLicensingSteps/state/slices/licensingModalSlice";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { PeopleType } from "features/Library/PeopleAssignments/types";
import { FeatureFlags } from "../../../../featureFlags";
import { type LDProps } from "../../../LDProps";

interface Props extends WithRouterProps, PropsFromRedux, LDProps {
  renderContentSwitcher?: () => React.ReactElement;
}

interface State {
  selectedGroups: Group[];
  viewType: ViewType;
  isStepLoading: boolean;
  selectedUsersToAddIds: any[];
  focusedGroupId: number | null;
  deleteGroupsConfirmationModal: {
    show: boolean;
    groupIds: number[];
  };
  showAddToContentModal: boolean;
  showAddUsersModal: boolean;
  contentTypeToAdd: any;
  addGroupsToFlows: {
    groupIds: number[];
    flows: any[];
  };
  confirmation: {
    blocks: any[];
    priorityItems: any[];
    show: boolean;
    proceed: any;
  };
}

export class GroupsOverview extends Component<Props, State> {
  reloadListAddItems?: (enableSorting: boolean) => void;
  reloadGroupListItems?: (enableSorting: boolean) => void;
  additionalPurchaseEnabled: boolean;

  constructor(props: Props) {
    super(props);

    this.state = {
      selectedGroups: [],
      viewType: ViewType.LIST,
      isStepLoading: false,
      selectedUsersToAddIds: [],
      focusedGroupId: null,
      deleteGroupsConfirmationModal: {
        show: false,
        groupIds: [],
      },
      showAddToContentModal: false,
      showAddUsersModal: false,
      contentTypeToAdd: null,
      addGroupsToFlows: {
        groupIds: [],
        flows: [],
      },
      confirmation: {
        blocks: [],
        priorityItems: [],
        show: false,
        proceed: null,
      },
    };

    this.additionalPurchaseEnabled = !!this.props.flags?.[FeatureFlags.AdditionalPurchase];
  }

  onSelectedGroupsChanged = (ids: number[]) => {
    const { selectedGroups, viewType } = this.state;
    const updatedSelection = selectedGroups.filter((sg) => ids.includes(sg.id));

    const groups = viewType === ViewType.LIST ? this.props.groupsList.items : this.props.groupsGrid.items;

    const addedGroups = groups.filter((g) => ids.includes(g.id) && selectedGroups.every((sg) => sg.id !== g.id));

    this.setState({
      selectedGroups: [...updatedSelection, ...addedGroups],
    });
  };

  goToGroupCreation = () => {
    this.props.navigate("add");
  };

  goToEditGroup = (groupId: number) => {
    this.props.navigate(groupId.toString());
  };

  onDuplicateGroup = (groupId: number) => {
    this.onDuplicateGroups([groupId]);
  };

  onAddToContent = (groupId: number, contentType: AddPeopleToContentTypes) => {
    this.onAddToContentModal([groupId], contentType);
  };

  onDuplicateGroups = async (groupIds: number[]) => {
    this.onSelectedGroupsChanged([]);
    const params = {
      id: "DuplicateGroups",
      title: `Duplication of groups`,
      getMessageIds: async () => {
        const {
          data: { messageIds },
        } = await dataService.duplicateGroups(groupIds);
        return messageIds;
      },
      successTransientMessage: `${pluralize("Group", groupIds.length)} ${
        groupIds.length === 1 ? "has" : "have"
      } been duplicated!`,
      failureTransientMessage: `${pluralize("Group", groupIds.length)} duplicate failed!`,
    };

    const {
      backgroundTasksActions: { addOperationV1 },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    await backgroundTask.updateEntity(params, {
      addOperation: addOperationV1,
      sendTransientNotification,
    });
  };

  onDeleteGroups = (groupIds: number[]) => {
    this.setState({
      deleteGroupsConfirmationModal: {
        show: true,
        groupIds,
      },
    });
  };

  onDeleteGroupsConfirmed = async () => {
    const { groupIds } = this.state.deleteGroupsConfirmationModal;

    this.setState(
      {
        deleteGroupsConfirmationModal: {
          show: false,
          groupIds: [],
        },
      },
      () => {
        this.onSelectedGroupsChanged([]);
      },
    );

    const params = {
      id: "DeleteGroups",
      title: "Groups deletion",
      getMessageIdsV2: () => groupIds.map((groupId) => `GroupDeleted_${groupId}`),
      callApi: () => dataService.deleteGroups(groupIds),
      successTransientMessage: `${pluralize("Group", groupIds.length)} ${
        groupIds.length === 1 ? "has" : "have"
      } been deleted successfully.`,
      failureTransientMessage: `${pluralize("Group", groupIds.length)} deletion failed.`,
    };

    const {
      backgroundTasksActions: { addOperationV2 },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    await backgroundTask.updateEntityV2(params, {
      addOperation: addOperationV2,
      sendTransientNotification,
    });
  };

  onDeleteGroupsCanceled = () => {
    this.setState({
      selectedGroups: [],
      deleteGroupsConfirmationModal: {
        show: false,
        groupIds: [],
      },
    });
  };

  renderCreateGroupButton = () => {
    return (
      <Restricted
        permissions={[RolePermissions.GroupsCreate]}
        renderContent={(hasAnyPermission) => (
          <RestrictedByTooltip hasPermission={hasAnyPermission}>
            <Button
              onClick={this.goToGroupCreation}
              className="create-button group"
              content={"Create Group"}
              primary
              disabled={!hasAnyPermission}
            />
          </RestrictedByTooltip>
        )}
      />
    );
  };

  onlyManualGroupsSelected = () => {
    const { selectedGroups } = this.state;
    return some(selectedGroups) && selectedGroups.every((group) => group.isWithAutoEnroll === Enrollment.Manual);
  };

  onViewTypeChange = (viewType: ViewType) => {
    this.setState({ viewType });
  };

  loadUsersToAddPage = (
    skip: number,
    top: number,
    sortingColumnName: string,
    sortingDirection: SortingDirection,
    appliedFilter: any,
  ) => {
    const { fetchGroupUsersToAdd } = this.props.editGroupActions;
    const { addUsersSearch } = this.props;

    const filterParams = addUsersSearch ? appliedFilter : usersFilter.buildFilterQuery(appliedFilter);
    const orderParams = addUsersSearch
      ? userListUtils.formatOrderParamsV2(sortingColumnName, sortingDirection)
      : userListUtils.formatOrderParams(sortingColumnName, sortingDirection);

    fetchGroupUsersToAdd(this.selectedGroupIds(), skip, top, orderParams, filterParams, addUsersSearch);
  };

  onSecondStep = async (selectedIds: number[]) => {
    const groupIds = this.selectedGroupIds();
    const { shouldShowLicensingModal } = this.props;
    this.setState({ isStepLoading: true, selectedUsersToAddIds: selectedIds });

    const shouldShow = await shouldShowLicensingModal(groupIds, selectedIds, this.additionalPurchaseEnabled);

    this.setState({ isStepLoading: false });

    return shouldShow;
  };

  selectedGroupIds = () => {
    const { focusedGroupId, selectedGroups } = this.state;
    return focusedGroupId ? [focusedGroupId] : selectedGroups.map((x) => x.id);
  };

  onAddUsersConfirm = async (notificationSettings?: NotifyStepSettings) => {
    const { selectedUsersToAddIds } = this.state;
    const groupIds = this.selectedGroupIds();
    this.onCloseAddUsersModal();
    this.onSelectedGroupsChanged([]);
    this.props.addUsersToGroups(selectedUsersToAddIds, groupIds, notificationSettings);
  };

  onAddGroupMembers = (groupId: number) => {
    this.setState({ focusedGroupId: groupId }, this.showAddUsersModal);
  };

  onCloseAddUsersModal = () => {
    this.props.editGroupActions.resetAddMembersSearch();
    this.props.resetLicensingModal();
    this.setState({ selectedGroups: [], focusedGroupId: null, showAddUsersModal: false });
  };

  showAddUsersModal = () => {
    this.setState({ showAddUsersModal: true });
  };

  createReloadListAddItems = (reloadListItems: (enableSorting: boolean) => void) => {
    this.reloadListAddItems = reloadListItems;
  };

  onSearchUsersAddChanged = (search: string) => {
    this.props.editGroupActions.setAddMembersSearch(search);
    this.reloadListAddItems?.(isEmpty(search));
  };

  renderUsersAddModal = () => {
    const {
      usersToAdd: { items, itemsCount, isLoading },
      addUsersSearch,
    } = this.props;

    const { isStepLoading, showAddUsersModal, selectedGroups } = this.state;

    const selectedGroupIds = this.selectedGroupIds();
    const allGroupsAware = selectedGroups.every((g) => g.isAware === Awareness.Aware);
    return (
      <UsersAssignmentModal
        loadPage={this.loadUsersToAddPage}
        usersAmount={itemsCount}
        isListLoading={isLoading}
        isStepLoading={isStepLoading}
        users={items as unknown as User[]}
        onSecondStep={this.onSecondStep}
        onConfirm={this.onAddUsersConfirm}
        showModal={showAddUsersModal}
        onCancel={this.onCloseAddUsersModal}
        assignmentEntityType="group"
        focusedEntityIds={selectedGroupIds}
        usersAssignments={{
          items: items.flatMap((item) =>
            item.assignedGroupIds.map((groupId) => ({
              userId: item.id,
              groupId,
            })),
          ),
          comparer: (assignment, id) => assignment.groupId === +id,
        }}
        multipleAssignment={selectedGroupIds.length > 1}
        showRoleFilter
        search={addUsersSearch}
        onSearchChanged={this.onSearchUsersAddChanged}
        setReloadListItems={this.createReloadListAddItems}
        disableNotifications={!allGroupsAware}
      />
    );
  };

  onAddToContentModal = (groupIds: number[], contentType: AddPeopleToContentTypes) => {
    this.props.addToContentActions.setSelectedContentType(contentType);
    this.props.addToContentActions.setSelectedPeopleIds(groupIds);
    this.props.addToContentActions.setSelectedPeopleType(AssignmentPeopleContext.Group);
    this.onSelectedGroupsChanged([]);
  };

  renderAddToContentModal = () => {
    const { showAddToContentModal } = this.props;

    return showAddToContentModal && <ContentAssignmentModal peopleType={PeopleType.Group} />;
  };

  onSearchGroupsChanged = (search: string) => {
    this.props.setGroupsFilterActions.setSearch(search);
    this.reloadGroupListItems?.(isEmpty(search));
  };

  isFiltered = () => {
    const { appliedGroupsFilter, searchValue } = this.props;
    return !isEmpty(appliedGroupsFilter) || !isEmpty(searchValue);
  };

  render() {
    const {
      groupsList,
      groupsGrid,
      appliedGroupsFilter,
      searchValue,
      filterActions,
      filterOptions,
      accountId,
      groupsOverviewActions: { fetchGroups, fetchGroupsLazy, resetGridItems },
      setGroupsFilterActions,
    } = this.props;

    const { selectedGroups, deleteGroupsConfirmationModal, viewType } = this.state;

    return (
      <section className="nested-content groups">
        <GroupsOverviewHeader
          onAddToContentModal={this.onAddToContentModal}
          onDeleteGroups={this.onDeleteGroups}
          showAddUsersModal={this.showAddUsersModal}
          onDuplicateGroups={this.onDuplicateGroups}
          onlyManualGroupsSelected={this.onlyManualGroupsSelected}
          renderCreateButton={this.renderCreateGroupButton}
          selectedGroups={selectedGroups}
        />
        <GroupList
          viewType={viewType}
          accountId={accountId}
          searchValue={searchValue}
          onSearchChange={this.onSearchGroupsChanged}
          groupsList={groupsList}
          groupsGrid={groupsGrid}
          fetchGroups={fetchGroups}
          fetchGroupsLazy={fetchGroupsLazy}
          setGroupsFilter={setGroupsFilterActions.setAppliedFilter}
          resetGroupsFilter={setGroupsFilterActions.resetAppliedFilter}
          appliedGroupsFilter={appliedGroupsFilter}
          selectedIds={selectedGroups.map((g) => g.id)}
          onSelectedGroupsChanged={this.onSelectedGroupsChanged}
          noResultsContent={() => (
            <GroupsNoResults createGroupButton={this.renderCreateGroupButton()} filtered={this.isFiltered()} />
          )}
          resetGridItems={resetGridItems}
          onViewTypeChange={this.onViewTypeChange}
          filterActions={filterActions}
          filterOptions={filterOptions}
          buttonHandlers={{
            onEdit: this.goToEditGroup,
            onDuplicate: this.onDuplicateGroup,
            onAddMembers: this.onAddGroupMembers,
            onDelete: (groupId) => this.onDeleteGroups([groupId]),
            onAddToContent: this.onAddToContent,
          }}
          renderContentSwitcher={this.props.renderContentSwitcher}
          withEnrollmentFilter={false}
          setReloadListItems={(reloadListItems: any) => (this.reloadGroupListItems = reloadListItems)}
        />
        <DeleteGroupsConfirmationModal
          groupsCount={deleteGroupsConfirmationModal.groupIds.length}
          open={deleteGroupsConfirmationModal.show}
          onCancel={this.onDeleteGroupsCanceled}
          onContinue={this.onDeleteGroupsConfirmed}
        />
        {this.renderUsersAddModal()}
        {this.renderAddToContentModal()}
      </section>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
  permissions: state.userProfile.permissions,
  groupsList: state.people.groupsOverview.groupsList,
  groupsGrid: state.people.groupsOverview.groupsGrid,
  filterOptions: state.people.groupsOverview.filterOptions,
  appliedGroupsFilter: state.people.groupsOverview.filterSlice.appliedFilter,
  searchValue: state.people.groupsOverview.filterSlice.search,
  usersToAdd: state.people.editGroup.editPeople.usersToAdd,
  showAddToContentModal: state.people.addToContentModal.showModal,
  addUsersSearch: state.people.editGroup.addMembersSearch,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  groupsOverviewActions: bindActionCreators(groupsOverviewActionsRedux, dispatch),
  backgroundTasksActions: bindActionCreators(backgroundTasksActionsRedux, dispatch),
  notificationsActions: bindActionCreators(notificationsActionsRedux, dispatch),
  editGroupActions: bindActionCreators(editGroupActionsRedux, dispatch),
  filterActions: bindActionCreators(filterActionsRedux, dispatch),
  setGroupsFilterActions: bindActionCreators(groupFiltersActionsRedux, dispatch),
  addToContentActions: bindActionCreators(addToContentActionsRedux, dispatch),
  addUsersToGroups: bindActionCreators(addUsersToGroups, dispatch),
  shouldShowLicensingModal: bindAction(shouldShowModalForGroupMembership, dispatch),
  resetLicensingModal: bindAction(reset, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(withRouter(withLDConsumer()(GroupsOverview)));

export default ConnectedComponent;
